// Delivery utilities for determining available shipping options based on address

/**
 * Determines available delivery options based on shipping address
 * @param {Object} shippingAddress - The shipping address object
 * @returns {Array} Array of available delivery options
 */
export function getAvailableDeliveryOptions(shippingAddress) {
  const { country, state } = shippingAddress;
  
  // International addresses (not Indonesia) -> DHL only
  if (country !== 'Indonesia') {
    return ['DHL'];
  }
  
  // Indonesia, Bali -> Lion Parcel + Pickup
  if (country === 'Indonesia' && state === 'Bali') {
    return ['LION_PARCEL', 'PICKUP'];
  }
  
  // Indonesia, not Bali -> Lion Parcel only
  if (country === 'Indonesia' && state !== 'Bali') {
    return ['LION_PARCEL'];
  }
  
  // Fallback - shouldn't happen but just in case
  return ['DHL'];
}

/**
 * Delivery options configuration with prices and details
 */
export const DELIVERY_OPTIONS = {
  DHL: {
    id: 'DHL',
    name: 'DHL International Express',
    description: 'Fast international shipping with tracking',
    baseCost: 25,
    perKgCost: 10,
    estimatedDays: '5-7 business days',
    trackingAvailable: true,
    regions: ['international'],
    icon: '✈️'
  },
  LION_PARCEL: {
    id: 'LION_PARCEL',
    name: 'Lion Parcel',
    description: 'Reliable delivery across Indonesia',
    baseCost: 10,
    perKgCost: 3,
    estimatedDays: '2-3 business days',
    trackingAvailable: true,
    regions: ['indonesia'],
    icon: '🚚'
  },
  PICKUP: {
    id: 'PICKUP',
    name: 'Pickup from Workshop',
    description: 'Collect your order from our Bali workshop',
    baseCost: 0,
    perKgCost: 0,
    estimatedDays: 'Same day once ready',
    trackingAvailable: false,
    regions: ['bali'],
    icon: '🏪'
  }
};

/**
 * Calculate delivery cost based on delivery option and weight
 * @param {string} deliveryOptionId - ID of the delivery option
 * @param {number} weightKg - Weight in kilograms (default 0.5kg for hats)
 * @returns {number} Total delivery cost
 */
export function calculateDeliveryCost(deliveryOptionId, weightKg = 0.5) {
  const option = DELIVERY_OPTIONS[deliveryOptionId];
  if (!option) {
    throw new Error(`Invalid delivery option: ${deliveryOptionId}`);
  }
  
  return option.baseCost + (option.perKgCost * weightKg);
}

/**
 * Get formatted delivery option for display
 * @param {string} deliveryOptionId - ID of the delivery option
 * @param {number} weightKg - Weight in kilograms
 * @returns {Object} Formatted delivery option object
 */
export function getDeliveryOptionDetails(deliveryOptionId, weightKg = 0.5) {
  const option = DELIVERY_OPTIONS[deliveryOptionId];
  if (!option) {
    throw new Error(`Invalid delivery option: ${deliveryOptionId}`);
  }
  
  const cost = calculateDeliveryCost(deliveryOptionId, weightKg);
  
  return {
    ...option,
    cost: cost,
    formattedCost: `$${cost.toFixed(2)}`,
    weightKg: weightKg
  };
}

/**
 * Get all available delivery options with details for a given address
 * @param {Object} shippingAddress - The shipping address object
 * @param {number} weightKg - Weight in kilograms
 * @returns {Array} Array of formatted delivery options
 */
export function getDeliveryOptionsForAddress(shippingAddress, weightKg = 0.5) {
  const availableOptions = getAvailableDeliveryOptions(shippingAddress);
  
  return availableOptions.map(optionId => 
    getDeliveryOptionDetails(optionId, weightKg)
  );
}

/**
 * Validate delivery option for given address
 * @param {string} deliveryOptionId - ID of the delivery option
 * @param {Object} shippingAddress - The shipping address object
 * @returns {boolean} True if valid, false otherwise
 */
export function isValidDeliveryOption(deliveryOptionId, shippingAddress) {
  const availableOptions = getAvailableDeliveryOptions(shippingAddress);
  return availableOptions.includes(deliveryOptionId);
}

/**
 * Get region type based on address
 * @param {Object} shippingAddress - The shipping address object
 * @returns {string} Region type: 'international', 'indonesia', or 'bali'
 */
export function getRegionType(shippingAddress) {
  const { country, state } = shippingAddress;
  
  if (country !== 'Indonesia') {
    return 'international';
  }
  
  if (country === 'Indonesia' && state === 'Bali') {
    return 'bali';
  }
  
  if (country === 'Indonesia' && state !== 'Bali') {
    return 'indonesia';
  }
  
  return 'international'; // fallback
}

/**
 * Get estimated delivery date
 * @param {string} deliveryOptionId - ID of the delivery option
 * @returns {string} Formatted estimated delivery date
 */
export function getEstimatedDeliveryDate(deliveryOptionId) {
  const option = DELIVERY_OPTIONS[deliveryOptionId];
  if (!option) {
    return 'Unknown';
  }
  
  const today = new Date();
  let deliveryDate;
  
  switch (deliveryOptionId) {
    case 'DHL':
      // 5-7 business days - use 7 for conservative estimate
      deliveryDate = addBusinessDays(today, 7);
      break;
    case 'LION_PARCEL':
      // 2-3 business days - use 3 for conservative estimate
      deliveryDate = addBusinessDays(today, 3);
      break;
    case 'PICKUP':
      return 'Ready for pickup once notified';
    default:
      deliveryDate = addBusinessDays(today, 7);
  }
  
  return deliveryDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Helper function to add business days to a date
 * @param {Date} date - Starting date
 * @param {number} days - Number of business days to add
 * @returns {Date} New date with business days added
 */
function addBusinessDays(date, days) {
  const result = new Date(date);
  let daysAdded = 0;
  
  while (daysAdded < days) {
    result.setDate(result.getDate() + 1);
    // Skip weekends (Saturday = 6, Sunday = 0)
    if (result.getDay() !== 0 && result.getDay() !== 6) {
      daysAdded++;
    }
  }
  
  return result;
}

/**
 * Get pickup location details
 * @returns {Object} Pickup location information
 */
export function getPickupLocation() {
  return {
    name: 'KALA HATS Workshop',
    address: {
      line1: 'Jl. Raya Ubud',
      city: 'Ubud',
      state: 'Bali',
      country: 'Indonesia',
      postalCode: '80571'
    },
    formattedAddress: 'Jl. Raya Ubud, Ubud, Bali, Indonesia 80571',
    hours: {
      monday: '9:00 AM - 6:00 PM',
      tuesday: '9:00 AM - 6:00 PM',
      wednesday: '9:00 AM - 6:00 PM',
      thursday: '9:00 AM - 6:00 PM',
      friday: '9:00 AM - 6:00 PM',
      saturday: '9:00 AM - 6:00 PM',
      sunday: '10:00 AM - 4:00 PM'
    },
    contact: {
      phone: '+62 813-3456-7890',
      email: 'workshop@kalahats.com'
    },
    description: 'Visit our workshop where SASTRAJIWA and our artisan team craft each piece of wearable rebellion.'
  };
}
